import * as path from 'node:path';
import { getBridgeRoot } from '../../files/cwd/index.js';
import { yieldToEventLoop } from '../../git/git-runtime.js';
import type { LogFn } from '../../log.js';
import { previewDeployBranchName } from '../worktree-layout-kind.js';
import type { PreviewWorktreeManager } from '../manager/preview-worktree-manager.js';
import type { SessionWorktreeManager } from '../manager/session-worktree-manager.js';
import { applySessionWipToPreviewCheckouts } from './apply-session-wip-to-preview-checkouts.js';
import { collectSessionRepoDeployStates } from './collect-session-repo-deploy-states.js';
import type { DeploySessionToPreviewEnvironmentResult } from './types.js';

export type { DeploySessionToPreviewEnvironmentResult } from './types.js';

export async function deploySessionToPreviewEnvironment(options: {
  sessionWorktreeManager: SessionWorktreeManager;
  previewWorktreeManager: PreviewWorktreeManager;
  sessionId: string;
  environmentId: string;
  log: LogFn;
}): Promise<DeploySessionToPreviewEnvironmentResult> {
  const { sessionWorktreeManager, previewWorktreeManager, log } = options;
  const sid = options.sessionId.trim();
  const eid = options.environmentId.trim();
  if (!sid || !eid) return { ok: false, error: 'sessionId and environmentId are required' };

  const deployBranch = previewDeployBranchName(eid);
  const bridgeRoot = path.resolve(getBridgeRoot());

  await previewWorktreeManager.ensureCached(eid);
  const existingPreviewParentPath = previewWorktreeManager.getPreviewParentPath(eid) ?? null;
  const existingPreviewCheckoutPaths = previewWorktreeManager.getRepoCheckoutPaths(eid) ?? [];

  const repoStates = await collectSessionRepoDeployStates({
    sessionWorktreeManager,
    sessionId: sid,
    deployBranch,
    bridgeRoot,
    log,
    previewParentPath: existingPreviewParentPath,
    previewCheckoutPaths: existingPreviewCheckoutPaths,
  });
  if (repoStates.length === 0) {
    return { ok: false, error: 'No git repositories found for session' };
  }

  await yieldToEventLoop();
  const previewParentPath = await previewWorktreeManager.createWorktrees(eid, deployBranch);
  if (!previewParentPath) {
    return { ok: false, error: 'Failed to create preview environment worktrees' };
  }

  await yieldToEventLoop();
  const applied = await applySessionWipToPreviewCheckouts({
    previewWorktreeManager,
    environmentId: eid,
    deployBranch,
    bridgeRoot,
    repoStates,
    log,
  });
  if (!applied.ok) {
    return { ok: false, error: applied.error };
  }

  log(
    `[worktrees] Deployed session ${sid.slice(0, 8)}… to preview ${eid.slice(0, 8)}… on branch ${deployBranch}.`,
  );
  return { ok: true, deployBranch, previewParentPath };
}
